import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/StatusBadge";
import { Plus, Edit, Trash2, Search, Package } from "lucide-react";
import { toast } from "sonner";

const empty = () => ({ item_name: "", item_type: "", user_id: "", notes: "" });

export default function Assignments() {
  const { user } = useAuth();
  const canWrite = ["admin", "accounting"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [filterUser, setFilterUser] = useState("all");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty());

  const load = async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (filterUser !== "all") params.user_id = filterUser;
    const { data } = await api.get("/assignments", { params });
    setItems(data);
  };

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, filterUser]);

  const openCreate = () => { setEdit(null); setForm(empty()); setOpen(true); };
  const openEdit = (a) => { setEdit(a); setForm({ ...a }); setOpen(true); };

  const save = async () => {
    if (!form.item_name.trim()) return toast.error("Zimmet adı gerekli");
    if (!form.item_type.trim()) return toast.error("Zimmet türü gerekli");
    if (!form.user_id) return toast.error("Personel seçin");
    try {
      const payload = {
        item_name: form.item_name.trim(),
        item_type: form.item_type.trim(),
        user_id: form.user_id,
        notes: form.notes || "",
      };
      if (edit) await api.put(`/assignments/${edit.id}`, payload);
      else await api.post("/assignments", payload);
      toast.success(edit ? "Güncellendi" : "Zimmet eklendi");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kayıt başarısız");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu zimmet kaydını silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/assignments/${id}`);
      toast.success("Silindi");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4">
      <div className="azd-card p-4 flex flex-wrap items-end gap-3 justify-between">
        <div className="flex flex-wrap gap-3 flex-1">
          <div className="relative min-w-[240px] flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Zimmet adı, türü veya personel ara..."
                   className="pl-9" data-testid="asg-search" />
          </div>
          <div className="min-w-[200px]">
            <Select value={filterUser} onValueChange={setFilterUser}>
              <SelectTrigger data-testid="asg-filter-user"><SelectValue placeholder="Personel" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Personeller</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {canWrite && (
          <Button onClick={openCreate} data-testid="asg-new"
                  className="bg-[#0B3676] hover:bg-[#08285A] text-white">
            <Plus size={16} className="mr-1" /> Yeni Zimmet
          </Button>
        )}
      </div>

      <div className="azd-card overflow-hidden">
        <div className="overflow-x-auto azd-scrollbar">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Zimmet Adı</th>
                <th className="text-left px-4 py-3 font-semibold">Tür</th>
                <th className="text-left px-4 py-3 font-semibold">Zimmetlenen Personel</th>
                <th className="text-left px-4 py-3 font-semibold">Notlar</th>
                <th className="text-left px-4 py-3 font-semibold">Kayıt Tarihi</th>
                {canWrite && <th className="text-right px-4 py-3 font-semibold">İşlem</th>}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={canWrite ? 6 : 5} className="p-8 text-center text-[#6B7280]">
                    Zimmet kaydı bulunmuyor
                  </td>
                </tr>
              ) : items.map((a) => (
                <tr key={a.id} data-testid={`asg-row-${a.id}`}
                    className="border-t border-[#E5E7EB] hover:bg-[#F6F8FB]/50">
                  <td className="px-4 py-3 font-semibold text-[#111827]">
                    <div className="flex items-center gap-2">
                      <Package size={14} className="text-[#0B3676]" />
                      {a.item_name}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="info">{a.item_type}</Badge>
                  </td>
                  <td className="px-4 py-3 font-semibold text-[#08285A]">{a.user_name}</td>
                  <td className="px-4 py-3 text-xs text-[#6B7280] max-w-xs truncate" title={a.notes}>
                    {a.notes || "-"}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#6B7280]">{fmtDateTime(a.created_at)}</td>
                  {canWrite && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(a)} data-testid={`asg-edit-${a.id}`}
                              className="p-1.5 text-[#6B7280] hover:text-[#0B3676] rounded">
                        <Edit size={14} />
                      </button>
                      <button onClick={() => remove(a.id)} data-testid={`asg-del-${a.id}`}
                              className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded ml-1">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{edit ? "Zimmet Düzenle" : "Yeni Zimmet"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <label className="block text-xs font-semibold mb-1.5">Zimmet Adı *</label>
              <Input value={form.item_name} onChange={(e) => set("item_name", e.target.value)}
                     placeholder="Örn: Dell Latitude 5420" data-testid="asg-name" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Tür *</label>
              <Input value={form.item_type} onChange={(e) => set("item_type", e.target.value)}
                     placeholder="Örn: Laptop, Telefon, Araç, Kredi Kartı" data-testid="asg-type" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Personel *</label>
              <Select value={form.user_id} onValueChange={(v) => set("user_id", v)}>
                <SelectTrigger data-testid="asg-user"><SelectValue placeholder="Personel seçin" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Notlar (opsiyonel)</label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
                        rows={3} placeholder="Seri no, marka, teslim tarihi vs." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={save} data-testid="asg-save"
                    className="bg-[#0B3676] hover:bg-[#08285A] text-white">
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
